import { Injectable } from '@angular/core';
import { OnboardingConfig } from '../../models/onboarding-config.model';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  /**
   * Apply brand color from API at runtime.
   * Also computes a readable on-brand text color.
   */
  applyBrandColor(brandHex: string): void {
    const brand = this.normalizeHex(brandHex);
    const onBrand = this.getHighContrastText(brand);

    const root = document.documentElement;
    root.style.setProperty('--brand-color', brand);
    root.style.setProperty('--on-brand-color', onBrand);
  }

  /**
   * Apply full introducer brand colors (primary, accent, secondary) as CSS custom properties.
   * Also generates lighter tint variants using color-mix for SVG sprite consumption.
   */
  applyBrandColors(colors: OnboardingConfig): void {
    const root = document.documentElement;

    if (colors.PrimaryColor) {
      const primary = this.normalizeHex(colors.PrimaryColor);
      root.style.setProperty('--brand-primary', primary);
      root.style.setProperty('--brand-primary-light', `color-mix(in srgb, ${primary} 30%, white)`);
      root.style.setProperty(
        '--brand-primary-lighter',
        `color-mix(in srgb, ${primary} 15%, white)`
      );
      root.style.setProperty('--brand-color', primary);
      root.style.setProperty('--on-brand-color', this.getHighContrastText(primary));
    }

    if (colors.AccentColor) {
      const accent = this.normalizeHex(colors.AccentColor);
      root.style.setProperty('--brand-accent', accent);
      root.style.setProperty('--brand-accent-light', `color-mix(in srgb, ${accent} 30%, white)`);
    }

    if (colors.SecondaryColor) {
      root.style.setProperty('--brand-secondary', this.normalizeHex(colors.SecondaryColor));
    }
  }

  private normalizeHex(hex: string): string {
    let h = hex.trim();
    if (!h.startsWith('#')) h = `#${h}`;
    if (h.length === 4) h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
    return h.toLowerCase();
  }

  private getHighContrastText(bg: string): string {
    const [r, g, b] = this.hexToRgb(bg);
    // YIQ luma
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? '#000000' : '#ffffff';
  }

  private hexToRgb(hex: string): [number, number, number] {
    const v = hex.replace('#', '');
    return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
  }
}
